import { useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { Task, TaskList } from "@/context/data.types";
import { fetchTaskList, fetchTasksForGroup, fetchTaskGroupCountsOnly, type GroupCountResponse } from "@/utils/taskhelper";
 
const isVendor = (userDetails: any) => {
  return userDetails?.roles?.[0]?.rolename?.split("|")[0] === "VENDOR";
};
 
let aggregatedTaskList: Task[] = [];
let refreshQueue: Promise<unknown> = Promise.resolve();

const useTask = () => {
  const { setIsLoading, setTaskCount } = useGlobalStateContext();
  const { userDetails } = useAuth();

  const refreshGetMyTask = useCallback(async ({requiredLoader = false, fetchTasks = true, groupNameToFetch, cachedGroupCountResponse}: {requiredLoader?: boolean; fetchTasks?: boolean; groupNameToFetch?: string; cachedGroupCountResponse?: GroupCountResponse} = {}) => {
    const value = isVendor(userDetails) ? "VENDOR" : userDetails?.userid ?? "";
    const isUserId = isVendor(userDetails) ? false : true;
    if(!value) return;

    const executeFetch = async () => {
      if(requiredLoader) {
        setIsLoading(true);
      }
      try {
        if (fetchTasks) {
          let taskData = await fetchTaskList(value, aggregatedTaskList, isUserId, userDetails?.roles, true, groupNameToFetch, cachedGroupCountResponse);
          if (taskData) {
            const allTasks = taskData.taskList.flatMap(group => group.tasks || []);
            aggregatedTaskList = allTasks;
            setTaskCount(taskData?.taskCount);
            return taskData;
          }
          else {
            setTaskCount(0);
          }
        } else {
          let taskData = await fetchTaskList(value, aggregatedTaskList, isUserId, userDetails?.roles, false);
          if (taskData) {
            setTaskCount(taskData?.taskCount);
            return taskData;
          } else {
            setTaskCount(0);
            return { taskList: [], taskCount: 0 };
          }
        }
      } catch (error: any) {
        console.log("Error fetching task list", error);
      } finally {
        if(requiredLoader) {
          setIsLoading(false);
        }
      }
    };

    const task = refreshQueue.then(executeFetch);
    refreshQueue = task.catch(() => {});
    return task;
  }, [userDetails, setIsLoading, setTaskCount]);

  type TaskGroupFilterParams = {
    assigneeUserId?: string[];
    assigneeRoleId?: string[];
    creationDate?: {
      startDate?: string;
      endDate?: string;
    };
  };

  const loadMoreTasksForGroup = useCallback(async (
    groupName: string,
    currentPage: number,
    currentTaskList: TaskList,
    search?: string,
    filterParams?: TaskGroupFilterParams
  ): Promise<TaskList> => {
    const value = isVendor(userDetails) ? "VENDOR" : userDetails?.userid ?? "";
    if(!value) return currentTaskList;

    try {
      const nextPage = currentPage + 1;
      const existingGroupTasks =
        currentTaskList.find((group) => group.contentTitle === groupName)?.tasks || [];
      const result = await fetchTasksForGroup(
        value,
        groupName,
        nextPage,
        existingGroupTasks,
        search,
        filterParams
      );

      aggregatedTaskList = [...aggregatedTaskList, ...result.tasks];

      const updatedTaskList = currentTaskList.map((group) => {
        if (group.contentTitle === groupName) {
          return {
            ...group,
            tasks: [...(group.tasks || []), ...result.tasks],
            hasMore: result.hasMore,
            currentPage: nextPage,
          };
        }
        return group;
      });

      return updatedTaskList;
    } catch (error: any) {
      console.log("Error loading more tasks for group", error);
      return currentTaskList;
    }
  }, [userDetails]);

  const fetchTasksForGroupName = useCallback(async (
    groupName: string,
    currentTaskList: TaskList,
    search?: string,
    filterParams?: {
      assigneeUserId?: string[];
      assigneeRoleId?: string[];
      creationDate?: {
        startDate?: string;
        endDate?: string;
      };
    }
  ): Promise<TaskList> => {
    const value = isVendor(userDetails) ? "VENDOR" : userDetails?.userid ?? "";
    if(!value) {
      return currentTaskList;
    }

    // Check if tasks are already loaded for this group (only if no search/filters are applied)
    // If search or filters are provided, always refetch from server
    // Note: search can be undefined (not provided), empty string (clearing search), or a value (searching)
    // We should refetch if:
    // - search is explicitly provided (even if empty string - means clearing search)
    // - filters are provided
    const group = currentTaskList.find(g => g.contentTitle === groupName);
    
    // search !== undefined means search parameter was explicitly provided (including empty string for clearing)
    const hasSearchOrFilters = search !== undefined || filterParams;
    
    if (group && group.tasks && group.tasks.length > 0 && !hasSearchOrFilters) {
      // Tasks already loaded and no search/filters, return current list
      return currentTaskList;
    }

    setIsLoading(true);
    try {
      const result = await fetchTasksForGroup(value, groupName, 0, aggregatedTaskList, search, filterParams);
      
      // Update aggregated list
      aggregatedTaskList = [...aggregatedTaskList, ...result.tasks];
      
      // Update the specific group in task list
      // Note: Keep the original count (from group count API) for pagination calculation
      // Don't update count with filtered count - use original tasksCount for pagination
      const updatedTaskList = currentTaskList.map((group) => {
        if (group.contentTitle === groupName) {
          return {
            ...group,
            tasks: result.tasks,
            currentPage: 0,
            hasMore: result.hasMore,
            // Keep original count from group count API - don't overwrite with filtered count
            // This ensures pagination uses the original tasksCount (e.g., 361) not filtered count (e.g., 20)
          };
        }
        return group;
      });
      
      return updatedTaskList;
    } catch (error: any) {
      console.log("Error fetching tasks for group", error);
      return currentTaskList;
    } finally {
      setIsLoading(false);
    }
  }, [userDetails, setIsLoading]);

  const fetchTasksForPage = useCallback(async (
    groupName: string,
    pageNo: number,
    currentTaskList: TaskList,
    search?: string,
    filterParams?: {
      assigneeUserId?: string[];
      assigneeRoleId?: string[];
      creationDate?: {
        startDate?: string;
        endDate?: string;
      };
    }
  ): Promise<TaskList> => {
    const value = isVendor(userDetails) ? "VENDOR" : userDetails?.userid ?? "";
    if(!value) return currentTaskList;

    setIsLoading(true);
    try {
      // Fetch tasks for the specific page (replace instead of accumulate)
      // Include search and filter parameters if provided (server handles filtering)
      const result = await fetchTasksForGroup(value, groupName, pageNo, [], search, filterParams);
      
      // Update the specific group in task list with new page data
      // Note: Keep the original count (from group count API) for pagination calculation
      const updatedTaskList = currentTaskList.map((group) => {
        if (group.contentTitle === groupName) {
          return {
            ...group,
            tasks: result.tasks,
            currentPage: pageNo,
            hasMore: result.hasMore,
            // Keep original count from group count API - don't overwrite with filtered count
            // This ensures pagination uses the original tasksCount (e.g., 361) not filtered count (e.g., 20)
          };
        }
        return group;
      });
      
      return updatedTaskList;
    } catch (error: any) {
      console.log("Error fetching tasks for page", error);
      return currentTaskList;
    } finally {
      setIsLoading(false);
    }
  }, [userDetails, setIsLoading]);
 
  return { refreshGetMyTask, loadMoreTasksForGroup, fetchTasksForGroupName, fetchTasksForPage };
};
 
export default useTask;